import type { LocationTreeNode } from '../../../api/schedulingApis';
import {
  isProjectRootNode,
  formatLocationTypeLabel,
  collectLocationTypes,
  normalizeLocationNameInput,
} from '../../../location-hierarchy/locationTreeHelpers';

export {
  isProjectRootNode,
  formatLocationTypeLabel,
  collectLocationTypes,
  normalizeLocationNameInput,
};

export const FIXED_LOCATION_TYPE_PILLS = ['project', 'general', 'block', 'floor'] as const;

/**
 * When a location is renamed, append the former name to aliases (end of list)
 * if it is non-empty and not already present.
 */
export function appendFormerNameAsAlias(
  aliases: string[] | null | undefined,
  formerName: string | null | undefined,
  nextName: string | null | undefined,
): string[] {
  const existing = (aliases ?? [])
    .map((a) => String(a).trim())
    .filter(Boolean);
  const previous = String(formerName ?? '').trim();
  const next = String(nextName ?? '').trim();
  if (!previous || !next || previous === next) return existing;
  if (existing.some((a) => a === previous)) return existing;
  return [...existing, previous];
}

export function hasRootNode(nodes: LocationTreeNode[]): boolean {
  for (const n of nodes) {
    if (isProjectRootNode(n)) return true;
    if (n.child?.length && hasRootNode(n.child)) return true;
  }
  return false;
}

export function findNodeById(nodes: LocationTreeNode[], id: string): LocationTreeNode | null {
  for (const n of nodes) {
    if (String(n.location_id) === String(id)) return n;
    const inChild = findNodeById(n.child ?? [], id);
    if (inChild) return inChild;
  }
  return null;
}

/** Depth-first preorder flatten (same order as the tree list). */
export function flattenLocationTree(nodes: LocationTreeNode[]): LocationTreeNode[] {
  const out: LocationTreeNode[] = [];
  function walk(list: LocationTreeNode[]) {
    for (const n of list) {
      out.push(n);
      if (n.child?.length) walk(n.child);
    }
  }
  walk(nodes);
  return out;
}

/**
 * Node immediately above `nodeId` in the tree list (DFS order).
 * Used after delete to keep the details panel on a sensible neighbor.
 */
export function findImmediatePreviousNode(
  nodes: LocationTreeNode[],
  nodeId: string,
): LocationTreeNode | null {
  const flat = flattenLocationTree(nodes);
  const idx = flat.findIndex((n) => String(n.location_id) === String(nodeId));
  if (idx <= 0) return null;
  return flat[idx - 1] ?? null;
}

/** Sibling list context for indent / outdent (Tab / ⇧ Tab). */
export function findNodeSiblingContext(
  nodes: LocationTreeNode[],
  nodeId: string,
): {
  node: LocationTreeNode;
  parentId: string | null;
  siblings: LocationTreeNode[];
  index: number;
} | null {
  const id = String(nodeId);
  function walk(
    list: LocationTreeNode[],
    parentId: string | null,
  ): {
    node: LocationTreeNode;
    parentId: string | null;
    siblings: LocationTreeNode[];
    index: number;
  } | null {
    for (let i = 0; i < list.length; i++) {
      const n = list[i];
      if (String(n.location_id) === id) {
        return { node: n, parentId, siblings: list, index: i };
      }
      const hit = walk(
        n.child ?? [],
        n.location_id != null ? String(n.location_id) : null,
      );
      if (hit) return hit;
    }
    return null;
  }
  return walk(nodes, null);
}

export function findProjectRoot(nodes: LocationTreeNode[]): LocationTreeNode | null {
  for (const n of nodes) {
    if (isProjectRootNode(n)) return n;
    const nested = findProjectRoot(n.child ?? []);
    if (nested) return nested;
  }
  return null;
}

export function countNodesByType(nodes: LocationTreeNode[]): Record<string, number> {
  const counts: Record<string, number> = {};
  function walk(list: LocationTreeNode[]) {
    for (const n of list) {
      const key = String(n.location_type || 'general').toLowerCase();
      counts[key] = (counts[key] ?? 0) + 1;
      if (n.child?.length) walk(n.child);
    }
  }
  walk(nodes);
  return counts;
}

/** Fixed pills first (incl. zeros), then any extra types present in the tree. */
export function buildTypePillCounts(nodes: LocationTreeNode[]): Array<{ type: string; count: number }> {
  const counts = countNodesByType(nodes);
  const seen = new Set<string>();
  const out: Array<{ type: string; count: number }> = [];
  for (const t of FIXED_LOCATION_TYPE_PILLS) {
    seen.add(t);
    out.push({ type: t, count: counts[t] ?? 0 });
  }
  for (const key of Object.keys(counts).sort()) {
    if (seen.has(key)) continue;
    out.push({ type: key, count: counts[key] });
  }
  return out;
}

export function getAncestorBreadcrumb(
  nodes: LocationTreeNode[],
  nodeId: string,
): LocationTreeNode[] {
  const path: LocationTreeNode[] = [];
  function walk(list: LocationTreeNode[], trail: LocationTreeNode[]): boolean {
    for (const n of list) {
      const next = [...trail, n];
      if (String(n.location_id) === String(nodeId)) {
        path.push(...next);
        return true;
      }
      if (n.child?.length && walk(n.child, next)) return true;
    }
    return false;
  }
  walk(nodes, []);
  return path;
}

export function countDirectChildren(node: LocationTreeNode): number {
  return node.child?.length ?? 0;
}

export function countBranchNodes(node: LocationTreeNode): number {
  return (node.child ?? []).reduce((acc, c) => acc + 1 + countBranchNodes(c), 0);
}

/**
 * Expand name templates like "Level {01}" into sequenced names.
 * `{01}` / `{1}` / `{001}` control zero-padding width.
 */
export function expandNameTemplate(template: string, count: number): string[] {
  const raw = template.trim() || 'Item {01}';
  const match = raw.match(/\{(0*)(\d+)\}/);
  if (!match) {
    return Array.from({ length: count }, (_, i) => `${raw} ${i + 1}`);
  }
  const width = Math.max(match[1].length + match[2].length, match[0].length - 2);
  const start = Number(match[2]) || 1;
  return Array.from({ length: count }, (_, i) => {
    const n = start + i;
    const padded = String(n).padStart(width, '0');
    return raw.replace(match[0], padded);
  });
}

export function previewNameTemplate(template: string, count: number): string {
  const names = expandNameTemplate(template, count);
  if (names.length === 0) return '';
  if (names.length <= 3) return names.join(', ');
  return `${names[0]}, ${names[1]} … ${names[names.length - 1]}`;
}

/** Filter tree to nodes matching type (keeps ancestors of matches). */
export function filterTreeByLocationType(
  nodes: LocationTreeNode[],
  locationType: string | null,
): LocationTreeNode[] {
  if (!locationType) return nodes;
  const want = locationType.toLowerCase();

  function filterList(list: LocationTreeNode[]): LocationTreeNode[] {
    const out: LocationTreeNode[] = [];
    for (const n of list) {
      const kids = filterList(n.child ?? []);
      const selfMatch = String(n.location_type || '').toLowerCase() === want;
      if (selfMatch || kids.length > 0) {
        out.push({ ...n, child: kids });
      }
    }
    return out;
  }

  return filterList(nodes);
}
